import type { EditorAction, EditorState } from "../../editor/reducers";
import type { Dispatch } from "react";
import type { CellState } from "../../tables/types";
import type {
  TableHeaderCellScopeInput,
  TableRegionUpsertFormFragment,
} from "../../client/generated";
import { CSS_IN_TO_PX } from "../../editor/utils";
import { useApolloClient } from "@apollo/client";
import { encodeCellIdentifier } from "../../tables/utils";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { TableEditor } from "../../tables/TableEditor";
import { Separator } from "@acdc2/ui/components/separator";
import {
  TableHeaderCellScopeType,
  useCreateTableCellRegionMutation,
  useCreateTableDataRegionMutation,
  useCreateTableHeaderRegionMutation,
  useCreateTableRegionMutation,
  useDeleteTableCellRegionMutation,
} from "../../client/generated";

type Props = {
  fragment: TableRegionUpsertFormFragment;
  state: EditorState;
  dispatch: Dispatch<EditorAction>;
};

export function TableRegionUpsertForm({ fragment, state, dispatch }: Props) {
  const apolloClient = useApolloClient();

  const [createTableRegion] = useCreateTableRegionMutation();
  const [deleteTableCellRegion] = useDeleteTableCellRegionMutation();
  const [createTableCellRegion] = useCreateTableCellRegionMutation();
  const [createTableDataRegion] = useCreateTableDataRegionMutation();
  const [createTableHeaderRegion] = useCreateTableHeaderRegionMutation();

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!state.table) return false;

    const tableWidth = state.table.cols.reduce(
      (sum, col) => sum + col.width,
      0,
    );
    const tableHeight = state.table.rows.reduce(
      (sum, row) => sum + row.height,
      0,
    );

    const tableTop = state.table.position.y;
    const tableLeft = state.table.position.x;
    const tableRight = tableLeft + tableWidth;
    const tableBottom = tableTop + tableHeight;

    const tableCellRegions = fragment.page.regions.filter((region) => {
      const regionTop =
        region.shape.aabb.top * fragment.page.height * CSS_IN_TO_PX;
      const regionLeft =
        region.shape.aabb.left * fragment.page.width * CSS_IN_TO_PX;
      const regionRight =
        region.shape.aabb.left +
        region.shape.aabb.width * fragment.page.width * CSS_IN_TO_PX;
      const regionBottom =
        region.shape.aabb.top +
        region.shape.aabb.height * fragment.page.height * CSS_IN_TO_PX;

      return (
        region.attributes.some(
          (attr) => attr.__typename === "TableCellAttribute",
        ) &&
        regionTop >= tableTop &&
        regionLeft >= tableLeft &&
        regionRight <= tableRight &&
        regionBottom <= tableBottom
      );
    });

    await Promise.allSettled(
      tableCellRegions.map((region) =>
        deleteTableCellRegion({ variables: { regionId: region.id } }),
      ),
    );

    const tableAttributeIds = fragment.attributes
      .filter((attr) => attr.__typename === "TableAttribute")
      .map((attr) => attr.id);

    const { errors, data } = await createTableRegion({
      variables: {
        regionId: fragment.id,
        cols: state.table.cols.map((col) => col.width / tableWidth),
        rows: state.table.rows.map((row) => row.height / tableHeight),
        excessAttributeIds: tableAttributeIds,
      },
    });

    if (
      errors ||
      data?.createTableAttribute.userErrors?.length ||
      data?.deleteAttributes.userErrors?.length
    ) {
      return false;
    }

    const cellRegionIdPairs: [CellState, string][] = [];
    const cellIdentifierToRegionidMapping: Record<string, string> = {};

    for (const cell of state.table.cells) {
      const cellLeft = state.table.cols
        .slice(0, cell.originCol)
        .reduce((sum, col) => sum + col.width, tableLeft);
      const cellTop = state.table.rows
        .slice(0, cell.originRow)
        .reduce((sum, row) => sum + row.height, tableTop);

      const cellWidth = state.table.cols
        .slice(cell.originCol, cell.originCol + cell.colSpan)
        .reduce((sum, col) => sum + col.width, 0);
      const cellHeight = state.table.rows
        .slice(cell.originRow, cell.originRow + cell.rowSpan)
        .reduce((sum, row) => sum + row.height, 0);

      const cellRight = cellLeft + cellWidth;
      const cellBottom = cellTop + cellHeight;

      const cellCreationResult = await createTableCellRegion({
        variables: {
          pageId: fragment.page.id,
          vertices: [
            {
              top: cellTop / (fragment.page.height * CSS_IN_TO_PX),
              left: cellLeft / (fragment.page.width * CSS_IN_TO_PX),
            },
            {
              top: cellBottom / (fragment.page.height * CSS_IN_TO_PX),
              left: cellLeft / (fragment.page.width * CSS_IN_TO_PX),
            },
            {
              top: cellBottom / (fragment.page.height * CSS_IN_TO_PX),
              left: cellRight / (fragment.page.width * CSS_IN_TO_PX),
            },
            {
              top: cellTop / (fragment.page.height * CSS_IN_TO_PX),
              left: cellRight / (fragment.page.width * CSS_IN_TO_PX),
            },
          ],
        },
      });

      if (!cellCreationResult.data?.createRegion.region) {
        return false;
      }

      cellRegionIdPairs.push([
        cell,
        cellCreationResult.data.createRegion.region.id,
      ]);
      cellIdentifierToRegionidMapping[encodeCellIdentifier(cell)] =
        cellCreationResult.data.createRegion.region.id;
    }

    for (const [cell, cellRegionId] of cellRegionIdPairs) {
      if (cell.role === "td") {
        const { errors, data } = await createTableDataRegion({
          variables: {
            regionId: cellRegionId,
            colSpan: cell.colSpan,
            rowSpan: cell.rowSpan,
          },
        });

        if (errors || data?.createTableCellAttribute.userErrors?.length) {
          return false;
        }
      } else {
        const regularScopeInputs: TableHeaderCellScopeInput[] = cell.scope
          ? [
              {
                type:
                  cell.scope === "col"
                    ? TableHeaderCellScopeType.Column
                    : TableHeaderCellScopeType.Row,
                isLocal: false,
              },
            ]
          : [];

        const headersScopeInputs: TableHeaderCellScopeInput[] =
          state.table.cells
            .map((otherCell) => {
              const cellIsHeaderOfOtherCell = otherCell.headers?.some(
                (header) =>
                  header.originCol === cell.originCol &&
                  header.originRow === cell.originRow,
              );

              if (!cellIsHeaderOfOtherCell) return undefined;

              const otherCellRegionId =
                cellIdentifierToRegionidMapping[
                  encodeCellIdentifier(otherCell)
                ];

              return {
                type: TableHeaderCellScopeType.Cell,
                isLocal: false,
                link: otherCellRegionId,
              };
            })
            .filter((scopeInput) => scopeInput !== undefined);

        const { errors, data } = await createTableHeaderRegion({
          variables: {
            regionId: cellRegionId,
            colSpan: cell.colSpan,
            rowSpan: cell.rowSpan,
            scopes: regularScopeInputs.concat(headersScopeInputs),
          },
        });

        if (
          errors ||
          data?.createTableCellAttribute.userErrors?.length ||
          data?.createTableHeaderCellAttribute.userErrors?.length
        ) {
          return false;
        }
      }
    }

    apolloClient.reFetchObservableQueries();
    return true;
  };

  if (!state.table) {
    return null;
  }

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <div className="-mx-4 -mt-4">
        <TableEditor
          dispatch={dispatch}
          fragment={fragment}
          tableState={state.table}
        />
        <Separator />
      </div>
    </RegionUpdateForm>
  );
}
